import React, { useMemo } from 'react';
import { AppointmentStatus, Timezone } from '@bb/domain/dist/types';
import styled from 'styled-components';
import {
  formatDateStrWithYearAndDayNoOrd,
  formatTimeStrLowerCase,
  formatDateStrNoOrd,
  getTimezoneCode,
} from 'utils';
import ListingImage from './ListingImage';
import { Link, Text } from '../Typography';
import Space from '../Space';
import theme from '../theme';
import { TIME_CHANGE_STATUSES } from '../../Router/pages/ShowingsCalendarPage/constants';

export interface ListingEntryProps {
  line1: string;
  line2?: string;
  listingId: string;
  dashboardId?: string;
  retsId?: string;
  needsAction?: boolean;
  thumbnail?: string;
  time?: string;
  timeEnd?: string;
  timezone?: Timezone;
  showTimezone?: boolean;
  type?: 'modal' | 'showing';
  originalTime?: string;
  originalTimeEnd?: string;
  status?: AppointmentStatus;
  tourName?: string;
  listingEntryExtraContent?: React.ReactNode;
  mlsNumber?: string;
}

const ListingEntry: React.FC<ListingEntryProps> = ({
  line1,
  line2 = '',
  needsAction,
  time,
  timeEnd,
  timezone,
  showTimezone = false,
  thumbnail,
  type,
  listingId,
  dashboardId,
  retsId,
  originalTime,
  originalTimeEnd,
  status,
  tourName,
  listingEntryExtraContent,
  mlsNumber,
}) => {
  const [startTime, endTime] =
    !tourName || !TIME_CHANGE_STATUSES.includes(status) || !originalTime
      ? [time, timeEnd]
      : [originalTime, originalTimeEnd];
  const timeStr = `${formatTimeStrLowerCase(
    startTime,
    timezone,
  )} - ${formatTimeStrLowerCase(endTime, timezone)}`;
  const dateStrWithYear = formatDateStrWithYearAndDayNoOrd(startTime, timezone);
  const dateStr = formatDateStrNoOrd(startTime, timezone);

  const listingViewUrl = useMemo(() => {
    if (dashboardId)
      return `/external/#/dash/${dashboardId}/listing/${listingId}`;

    if (retsId) return `/external/#/rets/${retsId}/listing`;

    return `/#/listing/${listingId}/view`;
  }, [dashboardId, listingId, retsId]);

  return (
    <StyledSpace
      key={`${listingId}-${time}`}
      align="center"
      size={12}
      data-cy="listing-entry-main"
    >
      {listingEntryExtraContent}
      <ListingImage size={type === 'showing' ? 64 : 40} thumbnail={thumbnail} />
      <StyledTextContainer>
        {mlsNumber && <Text color="textSecondary">MLS# {mlsNumber}</Text>}

        {type === 'modal' ? (
          <Text
            data-cy="listing-entry-address"
            color="textPrimary"
          >{`${line1} ${line2 || ''}`}</Text>
        ) : (
          <Link
            onClick={e => {
              e.stopPropagation();
            }}
            href={listingViewUrl}
            color="textPrimary"
            underlineOnHover
            strong={needsAction}
            data-cy="listing-entry-link"
          >
            {`${line1} ${line2 || ''}`}
          </Link>
        )}
        {time ? (
          <Text color={theme.gray8} data-cy="listing-entry-time">
            {type === 'modal' && `${dateStrWithYear}  • `}
            {type === 'showing' && `${dateStr}  • `}
            {timeStr}{' '}
            {showTimezone && timezone && `(${getTimezoneCode(timezone, time)})`}
          </Text>
        ) : (
          <Text color={theme.gray8} data-cy="listing-entry-no-time">
            No time selected
          </Text>
        )}
      </StyledTextContainer>
    </StyledSpace>
  );
};

export default ListingEntry;
export { ListingImage };

const StyledTextContainer = styled.div`
  line-height: 1.5;
  display: flex;
  flex-direction: column;
`;

const StyledSpace = styled(Space)`
  & div {
    z-index: 100;
  }
`;
